import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild, input, output, ElementRef, OnChanges, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { forkJoin, map, Subject, takeUntil } from 'rxjs';
import { ButtonComponent } from '@waseel-asp/tpa-waseel-lib-staging';
import { HttpRequestService } from '../../services/http-request/http-request.service';
import { FormService } from '../../../core/modules/admin-modules/pre-auth/preauth-request/services/form.service';
import { HelperService } from '../../services/common/helper.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IApprovalAttachment } from '../../../core/modules/admin-modules/pre-auth/preauth-request/attachments/models/attachments.model';
import { API } from '../../constants/api-url';
import { getButtonConfig } from '../../functions/config-function';
import { MimeTypeLabel, MimeTypeName } from '../../constants/mime-types';
import { ApprovalType } from '../../constants/apporval-type-enum';
import { DialogService } from '../../services/dialog/dialog.service';
import { DocumentService } from '../../services/common/document.service';
import { IPatientClaimReimbursementAttachment } from '../../../core/modules/patient-modules/claim-reimbursement/models/patient-claim-reimbursement.model';
import { ToastrHelperService } from '../../services/common/toast-helper.service';
import { IScreenPayload } from '../../models/common-models';

@Component({
  selector: 'app-common-attachment',
  standalone: true,
  imports: [
    CommonModule,
    MatIcon,
    MatListModule,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    ButtonComponent
  ],
  templateUrl: './common-attachment.component.html',
  styleUrl: './common-attachment.component.scss'
})
export class CommonAttachmentComponent implements OnInit, OnChanges, OnDestroy {
  private translation = inject(TranslateService);
  private httpRequestService = inject(HttpRequestService);
  private formService = inject(FormService);
  private helperService = inject(HelperService);
  private dialogService = inject(DialogService);
  private documentService = inject(DocumentService);
  private toastrHelperService = inject(ToastrHelperService);

  @ViewChild('fileInput', { static: false }) fileInput: ElementRef<HTMLInputElement>;

  attachmentsControl = input<FormControl>();
  attachmentIds = input<FormControl>();
  requestId = input<string>();
  approvalType = input<ApprovalType>();
  title = input<string>();
  singleFileUpload = input<boolean>(false);
  acceptFileType = input<string[]>(['application/pdf', 'image/jpg', 'image/jpeg']);
  isClearButtonCallback = input<boolean>(false);
  isDialog = input<boolean>(false);
  supportingInfoAttachmentIDs = input<string[]>();
  clearButtonCallBack = output<boolean>();
  isFollowUpRequest: boolean = false;
  isConvertToClaim: boolean = false;
  onDestroy$: Subject<void> = new Subject<void>();

  currentAttachmentIds: string[] = [];
  documentStore = {};
  isDragOver = false;
  isEditMode: boolean;
  newUploadedFiles: File[] = [];
  originalAttachmentIds: string[] = [];
  originalAttachMentUploadedList: File[] = [];
  uploadedFiles: { file: File, isNotDeletable?: boolean }[] = [];
  fileName: string = '';
  isProgrammaticChange = false;
  dataTransfer = new DataTransfer();
  acceptFileTypeLabel: string;
  attachmentApi: string;
  attachmentScreen: string;

  uploadButtonConfig = getButtonConfig(null, 'apply', 'raised', 'primary', this.translation.instant('COMMON.UPLOAD'), null, null, null, null, this.translation.instant('COMMON.UPLOAD'), 'above', ['btn-primary', 'btn', 'text-capitalize']);
  clearButtonConfig = getButtonConfig(null, 'clear', 'raised', 'primary', this.translation.instant('FORM_FIELD.CLEAR'), null, null, null, null, this.translation.instant('FORM_FIELD.CLEAR'), 'above', ['btn-secondary', 'btn', 'text-capitalize']);

  emitSelectedFile = output<File>();

  constructor() {
    this.isEditMode = this.formService.isEditModeSubject.getValue();
  }

  ngOnInit() {
    this.isFollowUpRequest = this.formService.isFollowUpSubject.getValue() ?? false;
    this.isConvertToClaim = this.formService.fromClaimReimbursement.getValue() ?? false;
    if (this.isConvertToClaim)
      this.attachmentApi = API.CLAIM_REIMBURSEMENT.GETATTACHMENT;
    else {
      this.attachmentApi = API.APPROVAL.APPROVAL_ATTACHMENT;
      this.attachmentScreen = this.helperService.getScreenFromApprovalType(this.approvalType());
    }

    if (this.isEditMode || this.isConvertToClaim) {
      const attachmentParams = this.isConvertToClaim ? undefined : { screen: this.attachmentScreen };
      this.httpRequestService.get(`${this.attachmentApi}/${this.isConvertToClaim ? this.formService.claimReimbursementId.value : this.requestId()}`, attachmentParams)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((response: IApprovalAttachment[] | IPatientClaimReimbursementAttachment[]) => {
          if (response) {
            // Filter response to get General Attachments (Exclude Supporting info Attachmnets)
            if (!this.isFollowUpRequest) {
              if (this.isConvertToClaim) {
                this.currentAttachmentIds = (response as IPatientClaimReimbursementAttachment[]).map(x => x.claimReimbursementAttachmentId);
                this.originalAttachmentIds = (response as IPatientClaimReimbursementAttachment[]).map(x => x.claimReimbursementAttachmentId);
              } else {
                this.currentAttachmentIds = (response as IApprovalAttachment[]).filter(x => !this.supportingInfoAttachmentIDs()?.includes(x.approvalAttachmentId))?.map(x => x.approvalAttachmentId);
                this.originalAttachmentIds = (response as IApprovalAttachment[]).filter(x => !this.supportingInfoAttachmentIDs()?.includes(x.approvalAttachmentId))?.map(x => x.approvalAttachmentId);
                this.formService.updatesupportingInfoAttachmentNames((response as IApprovalAttachment[]).filter(x => this.supportingInfoAttachmentIDs()?.includes(x.approvalAttachmentId)).map(y => y.attachmentName + '.' + y.attachmentType));
              }
              this.attachmentIds().setValue(this.currentAttachmentIds);
            }
            const cardAttachmentId = (response as IApprovalAttachment[])?.filter(x => !this.supportingInfoAttachmentIDs()?.includes(x.approvalAttachmentId));
            cardAttachmentId.forEach(attachment => {
              this.originalAttachMentUploadedList.push(this.convertToFile(attachment));
            });
            if (!this.isFollowUpRequest)
              this.handleFiles(this.originalAttachMentUploadedList, true);
            else
              this.getFiles(this.originalAttachMentUploadedList);
          }
        });
    }
  }

  ngOnChanges(): void {
    this.acceptFileTypeLabel = this.translation.instant('PREAUTH.PREAUTH_REQUEST.ATTACHMENTS.ALLOWED_TYPE', { fileTypes: this.documentService.getFileTypesByMIMEType(this.acceptFileType()) });
  }

  private areFilesSame(): boolean {
    if (this.currentAttachmentIds.length !== this.originalAttachmentIds.length || this.newUploadedFiles.length > 0)
      return false;
    return true;
  }

  private handleFiles(files: File[], initialLoad?: boolean): void {
    // const currentFiles = this.uploadedFiles.map(x => x.file) || [];
    // const currentFilesTotalSize = currentFiles.reduce((sum, file) => sum + file.size, 0);
    const newFilesTotalSize = files.reduce((sum, file) => sum + file.size, 0);
    //handle files
    const dublicateFile = files.filter(file => this.formService.supportingInfoAttachmentNames.value.includes(file.name))[0]?.name;
    if (dublicateFile) {
      const errorMessage = this.translation.instant("COMMON.DUPLICATE_ATTACHMENT_FOUND", { filename: dublicateFile });
      this.toastrHelperService.showErrorMessage(errorMessage);
      return;
    }

    const inValidFile = [];
    if (!initialLoad) {
      for (const file of files) {
        const extStr = this.documentService.getFileExtension(file.name);
        const mimeTypeNames = Object.keys(MimeTypeName).filter(key => this.acceptFileType().find(mime => mime == MimeTypeName[key]));
        const allowedExt = mimeTypeNames.map(type => {
          const mimeType = MimeTypeName[type as keyof typeof MimeTypeName];
          return mimeType ? MimeTypeLabel[mimeType] : '';
        }).filter(label => label === extStr);
        if (!this.acceptFileType().includes(file.type) || (!allowedExt || allowedExt.length == 0))
          inValidFile.push(file.name);
      }
      if (inValidFile.length > 0) {
        this.toastrHelperService.showErrorMessage(this.translation.instant('PREAUTH.PREAUTH_REQUEST.ATTACHMENTS.INVALID_TYPE', { filetype: inValidFile }));
        files = files.filter(file => this.acceptFileType().includes(file.name));
      }
    }

    const updatedFiles = files.map(file => {
      file['ext'] = this.documentService.getFileExtension(file.name);
      return file;
    });

    const overRideFiles = [];

    if (this.isConvertToClaim) {
      let flag = false;
      updatedFiles.forEach(file => {
        if (this.uploadedFiles.slice(0, this.currentAttachmentIds.length).some(x => x.file.name === file.name)) {
          const fileName = this.uploadedFiles.slice(0, this.currentAttachmentIds.length).find(x => x.file.name === file.name).file.name;
          this.toastrHelperService.showErrorMessage(this.translation.instant('PREAUTH.PREAUTH_REQUEST.ATTACHMENTS.ATTACHMENT_EXIST', { filename: fileName }));
          flag = true;
          return;
        }
      });
      if (flag)
        return;
    }

    const updatedValidFiles = updatedFiles.filter((file) => {
      if (this.uploadedFiles.some(x => x.file.name === file.name)) {
        overRideFiles.push(file);
        return false;
      } else
        return true;
    });

    if (overRideFiles.length === 0) {
      const status = this.checkMaxFileSize(newFilesTotalSize);
      if (status) {
        return;
      }
    }

    if (overRideFiles.length > 0) {
      this.dialogService.confirmActionDialog('COMMON.REPLACE_FILE_CONFIRMATION_MSG').subscribe(res => {
        if (res) {
          const updatedUploadsIndices = this.uploadedFiles.map((item, index) => ({ item, index })).filter(entry => overRideFiles.some(y => y.name === entry.item.file.name && y.ext === entry.item.file['ext'])).map(entry => entry.index);
          const existFileSize = this.formService.maxAttachmentSize.getValue();
          const previousUploadsSizeSum = this.uploadedFiles.filter((file, index) => updatedUploadsIndices.some(x => x === index) && ((this.currentAttachmentIds.length - 1) < index)).reduce((sum, file) => sum + file.file.size, 0);

          if ((existFileSize - (previousUploadsSizeSum / (1024 * 1024)) + newFilesTotalSize / (1024 * 1024)) < 25) {
            const currentFiles = this.attachmentsControl()?.value;
            currentFiles.filter((file, index) => !updatedUploadsIndices.some(x => x === index));
            this.uploadedFiles = this.uploadedFiles.filter((file, index) => !updatedUploadsIndices.some(x => x === index));
            this.currentAttachmentIds = this.currentAttachmentIds.filter((id, index) => !updatedUploadsIndices.some(x => x === index));
            this.attachmentIds().setValue(this.currentAttachmentIds);
            this.newUploadedFiles = this.newUploadedFiles.filter(file => !overRideFiles.some(y => y.name === file.name && y.ext === file['ext']));
            this.formService.maxAttachmentSize.next((existFileSize - (previousUploadsSizeSum / (1024 * 1024)) + (newFilesTotalSize / (1024 * 1024))));
          } else {
            this.toastrHelperService.showErrorMessage('PREAUTH.PREAUTH_REQUEST.ATTACHMENTS.MAX_ALL_SIZE');
            return;
          }

          this.uploadedFiles.push(
            ...overRideFiles.map(file => ({ file })),
            ...updatedValidFiles.map(file => ({ file }))
          );
          this.newUploadedFiles.push(
            ...overRideFiles,
            ...updatedValidFiles
          );
          this.attachmentsControl().setValue(this.newUploadedFiles);
          this.formService.updateAttachmentStatus(this.areFilesSame());
          this.formService.updatecommonAttachmentNames(updatedValidFiles.map(res => res.name));
          this.formService.updatecommonAttachmentNames(this.uploadedFiles.map(res => res.file.name));
        }
      });
    } else {
      if (initialLoad && this.isConvertToClaim)
        this.uploadedFiles.push(...updatedValidFiles.map(file => ({ file, isNotDeletable: true })));
      else
        this.uploadedFiles.push(...updatedValidFiles.map(file => ({ file })));

      if (!initialLoad) {
        this.newUploadedFiles.push(...updatedValidFiles);
        this.attachmentsControl().setValue(this.newUploadedFiles);
      }
      this.formService.updateAttachmentStatus(this.areFilesSame());
      this.formService.updatecommonAttachmentNames(this.uploadedFiles.map(res => res.file.name));
    }
  }

  private checkMaxFileSize(newFilesTotalSize: any): boolean {
    const existFileSize = this.formService.maxAttachmentSize.getValue(); // Total Size of new attachments of both  (General & Supporting info)
    if (existFileSize + (newFilesTotalSize / (1024 * 1024)) > 25) {
      this.toastrHelperService.showErrorMessage('PREAUTH.PREAUTH_REQUEST.ATTACHMENTS.MAX_ALL_SIZE');
      return true;
    } else {
      this.formService.maxAttachmentSize.next(existFileSize + (newFilesTotalSize / (1024 * 1024)));
      return false;
    }
  }

  private downloadAttachmentsFromAPI(attachement, preview: boolean = false) {
    if (this.documentStore[attachement['attachmentName']]) {
      this.documentService.downloadFileFromBlob(this.documentStore[attachement['attachmentName']], attachement);
      return;
    }
    this.httpRequestService.get<Blob, IScreenPayload>(
      this.isConvertToClaim ? `${API.CLAIM_REIMBURSEMENT.ATTACHMENT}/download/${attachement.attachmentId}` : `${API.APPROVAL.DOWNLOAD_APPROVAL_ATTACHMENTS}/request/${attachement.attachmentId}/download`,
      this.isConvertToClaim ? undefined : { screen: this.attachmentScreen }, undefined, undefined, 'blob')
      .pipe(takeUntil(this.onDestroy$)).subscribe((response: Blob) => {
        if (response) {
          this.documentStore[attachement['attachmentName']] = response;
          preview ? this.documentService.previewFile(response, attachement.attachmentName + "." + attachement.attachmentType) : this.documentService.downloadFileFromBlob(response, attachement);
        }
      });
  }

  convertToFile(attachment: IApprovalAttachment | IPatientClaimReimbursementAttachment): File {
    const file: File = new File([""], attachment.attachmentName + "." + attachment.attachmentType, { type: MimeTypeName[attachment.attachmentType] });
    file['attachmentId'] = this.isConvertToClaim ? (attachment as IPatientClaimReimbursementAttachment).claimReimbursementAttachmentId : (attachment as IApprovalAttachment).approvalAttachmentId;
    file['attachmentType'] = attachment.attachmentType;
    file['attachmentName'] = attachment.attachmentName;
    file['fileSize'] = attachment.fileSize;
    return file;
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);

    if (this.isProgrammaticChange) {
      this.isProgrammaticChange = false;
      this.fileInput.nativeElement.files = this.dataTransfer.files;
      this.dataTransfer = new DataTransfer();
      return;
    }

    if (this.isFileTypeValid(files)) {
      const filteredFiles = files.filter(f => f.size !== 0);
      const zeroKbNames = files.filter(f => f.size === 0).map(f => f.name);

      if (zeroKbNames.length)
        this.toastrHelperService.showErrorMessage(this.translation.instant('COMMON.ZERO_KB_WITH_NAME', { files: zeroKbNames.join(', ') }));

      if (filteredFiles.length && !this.singleFileUpload()) {
        this.handleFiles(filteredFiles);
        this.fileInput.nativeElement.value = '';
      } else
        this.fileName = filteredFiles[0]?.name;
    }
  }

  downloadAttachments(file: { file: File }) {
    if (file.file['attachmentId']) {
      const attachement = {
        attachmentId: file.file['attachmentId'],
        attachmentName: file.file['attachmentName'],
        attachmentType: file.file['attachmentType']
      };
      this.downloadAttachmentsFromAPI(attachement);
    } else
      this.helperService.downloadFile(file.file);
  }

  getFiles(file: File[]) {
    forkJoin(
      file.map(res => this.httpRequestService.get<Blob, IScreenPayload>(
        this.isConvertToClaim ? `${API.CLAIM_REIMBURSEMENT.ATTACHMENT}/download/${res['attachmentId']}` : `${API.APPROVAL.DOWNLOAD_APPROVAL_ATTACHMENTS}/request/${res['attachmentId']}/download`,
        this.isConvertToClaim ? undefined : { screen: this.attachmentScreen }, undefined, undefined, 'blob'
      ).pipe(map(blob => new File([blob], res.name, { type: blob.type }))))
    ).pipe(takeUntil(this.onDestroy$)).subscribe((files: File[]) => {
      this.handleFiles(files);
      this.originalAttachMentUploadedList = [];
    });
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const files = Array.from(event.dataTransfer?.files || []);
    const zeroKbNames: string[] = [];
    const filteredFiles = files.filter(file => {
      if (file.size === 0) {
        zeroKbNames.push(file.name);
        return false;
      }
      return true;
    });

    if (zeroKbNames.length) {
      this.toastrHelperService.showErrorMessage(
        this.translation.instant('COMMON.ZERO_KB_WITH_NAME', {
          files: zeroKbNames.join(', ')
        })
      );
    };

    if (filteredFiles.length && !this.singleFileUpload()) {
      this.handleFiles(filteredFiles);
    } else {
      if (filteredFiles.length && this.isFileTypeValid(filteredFiles)) {
        this.dataTransfer = new DataTransfer();
        filteredFiles.forEach(file => this.dataTransfer.items.add(file));
        this.isProgrammaticChange = true;
        this.fileInput.nativeElement.files = this.dataTransfer.files;
        this.fileName = this.dataTransfer.files[0].name;
      }
    }
  }

  isFileTypeValid(files: File[]) {
    for (const file of Array.from(files)) {
      if (!this.checkFileExtension(file)) {
        const key = Object.keys(MimeTypeName).filter(key => this.acceptFileType().includes(MimeTypeName[key]));
        this.toastrHelperService.showErrorMessage(this.translation.instant('COMMON.FILE_VALIDATION', { fileName: key?.join(', ') }));
        return false;
      }
    };
    return true;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(): void {
    this.isDragOver = false;
  }

  formatBytes(bytes: number): string {
    return this.helperService.formatBytes(bytes);
  }

  removeFile(index: number): void {
    this.dialogService.confirmActionDialog().subscribe(res => {
      if (res) {
        if (this.currentAttachmentIds.length > index) {
          this.currentAttachmentIds.splice(index, 1);
          this.attachmentIds().setValue(this.currentAttachmentIds);
        } else {
          const currentFiles = this.attachmentsControl()?.value;
          currentFiles.splice(index - this.uploadedFiles?.length - 1, 1);
          const existFileSize = this.formService.maxAttachmentSize.getValue();
          this.formService.maxAttachmentSize.next((existFileSize - (this.uploadedFiles[index].file.size / (1024 * 1024))));    // Remove size of deleted file from total Size of new attachments of both  (General & Supporting info)
        }
        this.uploadedFiles.splice(index, 1);
        this.formService.updatecommonAttachmentNames(this.uploadedFiles.map(res => res.file.name));
        this.formService.updateAttachmentStatus(this.areFilesSame());
      }
    });
  }

  preview(uploadedFile: { file: File }) {
    if (this.documentStore[uploadedFile.file['attachmentName']])
      this.documentService.previewFile(this.documentStore[uploadedFile.file['attachmentName']], uploadedFile.file.name);
    else if (uploadedFile.file['attachmentId']) {
      const attachement = {
        attachmentId: uploadedFile.file['attachmentId'],
        attachmentName: uploadedFile.file['attachmentName'],
        attachmentType: uploadedFile.file['attachmentType']
      };
      this.downloadAttachmentsFromAPI(attachement, true);
    } else {
      const blob = new Blob([uploadedFile.file], { type: uploadedFile.file.type }); // You can directly use the uploaded file
      this.documentService.previewFile(blob, uploadedFile.file.name);
    }
  }

  upload(event: Event) {
    event.stopPropagation();
    const input = this.fileInput.nativeElement as HTMLInputElement;
    this.emitSelectedFile.emit(input.files[0]);
  }

  clear(event: Event) {
    event.stopPropagation();
    this.clearAllFiles();
  }

  clearAllFiles() {
    this.isClearButtonCallback() && this.clearButtonCallBack.emit(true);
    this.fileInput.nativeElement.value = '';
    this.fileName = '';
    this.isProgrammaticChange = false;
    this.dataTransfer = new DataTransfer();
  }

  checkFileExtension(file: File) {
    return this.acceptFileType().includes(file.type);
  }

  uploadBoxClicked() {
    if (!(this.fileName && this.singleFileUpload)) {
      this.fileInput.nativeElement.click();
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
